import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search as SearchIcon, Plus, Edit, Trash2, Download, Upload } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';

const CausesPage = () => {
  const [causes, setCauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/causes');
      setCauses(response.data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await api.put(`/causes/${editItem.id}`, formData);
        toast.success('Cauza actualizata!');
      } else {
        await api.post('/causes', formData);
        toast.success('Cauza creata!');
      }
      setShowDialog(false);
      setEditItem(null);
      setFormData({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({ name: item.name, description: item.description || '' });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti?')) return;
    try {
      await api.delete(`/causes/${id}`);
      toast.success('Cauza stearsa!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/export/causes', { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.setAttribute('download', 'cauze.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export realizat cu succes!');
    } catch { toast.error('Eroare la export'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/import/causes', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error('Eroare la import'); }
    e.target.value = '';
  };

  const tableColumns = [
    {
      key: 'name', label: 'Denumire Cauza', sortable: true, filterable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <SearchIcon className="w-4 h-4 text-blue-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    { key: 'description', label: 'Descriere', sortable: false, filterable: true,
      render: (value) => <span className="text-slate-500 text-xs line-clamp-2">{value || '-'}</span>
    },
    {
      key: 'actions', label: 'Actiuni', sortable: false, filterable: false, width: 'w-24',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6" data-testid="causes-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Cauze</h1>
          <p className="text-slate-600 mt-1">Nomenclator cauze pentru diagnostic ({causes.length} total)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-10" onClick={handleExport} data-testid="causes-export-btn">
            <Download className="w-4 h-4 mr-1" />Export Excel
          </Button>
          <Button variant="outline" className="h-10" onClick={() => fileInputRef.current?.click()} data-testid="causes-import-btn">
            <Upload className="w-4 h-4 mr-1" />Import Excel
          </Button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setEditItem(null); setFormData({ name: '', description: '' }); } }}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-10"><Plus className="w-5 h-5 mr-2" />CAUZA NOUA</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{editItem ? 'Editeaza Cauza' : 'Adauga Cauza'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Denumire *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label>Descriere</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Anuleaza</Button>
                  <Button type="submit" className="btn-primary">{editItem ? 'ACTUALIZEAZA' : 'ADAUGA'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : (
        <DataTable data={causes} columns={tableColumns} pageSize={10} searchable={true} exportable={true} printable={true} selectable={true} onRowClick={handleEdit} emptyMessage="Nu exista cauze" />
      )}
    </div>
  );
};

export default CausesPage;
